import {
  Component,
  OnDestroy,
  Renderer2,
  afterNextRender,
  Signal,
  viewChild,
  computed,
  Inject,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { filter, Subscription } from 'rxjs';
import { ToastModule } from 'primeng/toast';

import { FooterComponent } from '@core/component/footer/footer.component';
import { SidebarComponent } from '@core/component/sidebar/sidebar.component';
import { TopbarComponent } from '@core/component/topbar/topbar.component';
import { CoreModule } from '@core/core.module';
import { LayoutService } from '@core/service/layout.service';
import { ConfiguratorComponent } from '@core/component/configurator/configurator.component';
import { CookieConsentComponent } from '@core/component/cookie-consent/cookie-consent.component';
import { isPlatformBrowser } from '@angular/common';
@Component({
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
  imports: [
    CoreModule,
    TopbarComponent,
    FooterComponent,
    SidebarComponent,
    ConfiguratorComponent,
    ToastModule,
    CookieConsentComponent,
  ],
})
export class AppShellComponent implements OnDestroy {
  protected appSidebar: Signal<SidebarComponent> = viewChild<SidebarComponent>('appSidebar');
  protected appTopBar: Signal<TopbarComponent> = viewChild<TopbarComponent>('appTopBar');
  protected isDarkTheme: Signal<boolean> = computed(() => this.layoutService.isDarkTheme());
  protected revealRail: Signal<boolean> = computed(
    () => this.layoutService.isReveal() && !this.layoutService.layoutState().anchored
  );
  protected showMainLayout: WritableSignal<boolean> = signal(true);

  private overlayMenuOpenSubscription: Subscription;
  private readonly showMainLayoutSubscription: Subscription;
  private menuOutsideClickListener: () => void;
  private menuScrollListener: () => void;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private readonly activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private readonly platform: string
  ) {
    this.showMainLayoutSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const data = this.leafRoute(this.activatedRoute).snapshot.data;
        this.showMainLayout.set(data?.showMainLayout ?? true);
      });

    afterNextRender(() => this.mainEvents());

    if (isPlatformBrowser(this.platform)) {
      if (this.layoutService.layoutConfig().darkTheme) {
        document.documentElement.dataset.theme = 'dark';
      } else {
        document.documentElement.dataset.theme = 'light';
      }
    }
  }

  public ngOnDestroy(): void {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }

    if (this.showMainLayoutSubscription) {
      this.showMainLayoutSubscription.unsubscribe();
    }
  }

  private leafRoute(route: ActivatedRoute): ActivatedRoute {
    let r = route;
    while (r.firstChild) {
      r = r.firstChild;
    }
    return r;
  }

  private outsideClickListener(event: MouseEvent): void {
    if (this.isOutsideClicked(event)) {
      this.hideMenu();
    }
  }

  private mainEvents(): void {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpenObservable.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', event =>
          this.outsideClickListener(event)
        );
      }

      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();

      const container = document.querySelector('.layout-content-wrapper');

      if (container) {
        container.scrollTop = 0;
      }
    });
  }

  private isOutsideClicked(event: MouseEvent): boolean {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.topbar-left > button');

    return !(
      sidebarEl?.isSameNode(event.target as Node) ||
      sidebarEl?.contains(event.target as Node) ||
      topbarButtonEl?.isSameNode(event.target as Node) ||
      topbarButtonEl?.contains(event.target as Node)
    );
  }

  private hideMenu(): void {
    this.layoutService.layoutState.update(prev => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  private blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  private unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replaceAll('blocked-scroll', '');
    }
  }
}
